import { AgentModelDTO } from '@api/fastAPI.schemas'

/** A model of the chat's model select; `icon` is shown before its label. */
export type ChatModelOption = AgentModelDTO & { icon?: string }

interface ModelAnimal {
  /** How capable the model is, 1 for the least; the select lists the lowest first. */
  capability: number
  name: string
  icon: string
  /** The model can read images: its animal is shown with a "+" ("Bär+"). */
  readsImages: boolean
}

/**
 * How users who are neither developers nor admins see the catalogue's models (the API's
 * `AGENT_MODELS` ids): as animals, the less capable a model the sweeter its
 * animal, with a "+" when it can read images. A model without an entry here
 * is not offered to them at all, so add one when the catalogue gains a model.
 */
export const MODEL_ANIMALS: Record<string, ModelAnimal> = {
  'openrouter:deepseek/deepseek-v4-pro-0813': {
    capability: 1,
    name: 'Maus',
    icon: '🐭',
    readsImages: false,
  },
  'openrouter:qwen/qwen3.6-plus': { capability: 2, name: 'Hamster', icon: '🐹', readsImages: true },
  'openrouter:z-ai/glm-5.3': { capability: 3, name: 'Hase', icon: '🐰', readsImages: false },
  'openrouter:moonshotai/kimi-k3': { capability: 4, name: 'Fuchs', icon: '🦊', readsImages: true },
  'openrouter:anthropic/claude-sonnet-5.5': {
    capability: 5,
    name: 'Wolf',
    icon: '🐺',
    readsImages: true,
  },
  'openrouter:anthropic/claude-opus-5.5': {
    capability: 6,
    name: 'Bär',
    icon: '🐻',
    readsImages: true,
  },
}

/**
 * The models a user may pick, least capable first: developers and admins see the whole
 * catalogue with its real names (models without an animal last, in catalogue
 * order), everyone else only the models with an animal, named after it.
 */
export const chatModelOptions = (
  models: AgentModelDTO[],
  showsRealNames: boolean,
): ChatModelOption[] => {
  const byCapability = [...models].sort(
    (a, b) =>
      (MODEL_ANIMALS[a.id]?.capability ?? Infinity) - (MODEL_ANIMALS[b.id]?.capability ?? Infinity),
  )
  if (showsRealNames) return byCapability
  return byCapability.flatMap(model => {
    const animal = MODEL_ANIMALS[model.id]
    if (!animal) return []
    const label = animal.readsImages ? `${animal.name}+` : animal.name
    return [{ id: model.id, label, icon: animal.icon }]
  })
}
